import { isValidPlutusUserId } from "./plutusUserId";

export const ADMIN_CUSTOMER_VIEW_ROUTE_STORE_KEY =
  "admin-customer-view:route-map:v1";
export const ADMIN_CUSTOMER_VIEW_ROUTE_TTL_MS = 30 * 60 * 1000;
export const ADMIN_CUSTOMER_VIEW_ROUTE_CAP = 10;
export const ADMIN_CUSTOMER_VIEW_KEY_PATTERN = /^[0-9a-f]{32}$/;

export interface StoredAdminCustomerViewRoute {
  viewKey: string;
  actorUserId: string;
  targetUserId: string;
  expiresAtMs: number;
  lastAccessedAtMs: number;
}

export interface StoredAdminCustomerViewRouteMap {
  version: 1;
  entries: StoredAdminCustomerViewRoute[];
}

export interface FrozenAdminCustomerViewRouteSubject {
  routeSubjectKey: string;
  targetUserId: string;
}

const STORE_FIELDS = new Set([
  "viewKey",
  "actorUserId",
  "targetUserId",
  "expiresAtMs",
  "lastAccessedAtMs",
]);

function isExactActorUserId(value: unknown): value is string {
  return typeof value === "string" && value.length >= 1 && value.length <= 256;
}

function isSafeNonNegativeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

export function isValidAdminCustomerViewKey(value: unknown): value is string {
  return typeof value === "string" && ADMIN_CUSTOMER_VIEW_KEY_PATTERN.test(value);
}

function nowMs(): number {
  return Date.now();
}

function generateViewKey(): string | null {
  if (typeof crypto === "undefined" || typeof crypto.getRandomValues !== "function") {
    return null;
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let hex = "";
  for (let i = 0; i < bytes.length; i += 1) {
    hex += bytes[i].toString(16).padStart(2, "0");
  }
  if (!ADMIN_CUSTOMER_VIEW_KEY_PATTERN.test(hex)) return null;
  return hex;
}

function clearDedicatedStore(): void {
  try {
    sessionStorage.removeItem(ADMIN_CUSTOMER_VIEW_ROUTE_STORE_KEY);
  } catch {
    // Storage failure must not throw.
  }
}

export function clearAdminCustomerViewRouteStore(): void {
  clearDedicatedStore();
}

/**
 * Validate the dedicated store against the actor established by authentication.
 * A store is actor-owned as a whole: retaining even one entry from another actor
 * would leave target identifiers from the prior session in this tab.
 */
export function reconcileAdminCustomerViewRouteStoreForActor(
  actorUserId: string
): boolean {
  if (!isExactActorUserId(actorUserId)) {
    failClosed();
    return false;
  }

  const now = nowMs();
  const existing = readStore(now);
  if (existing === "missing") return true;
  if (existing === "fail") {
    failClosed();
    return false;
  }
  if (existing.entries.some((entry) => entry.actorUserId !== actorUserId)) {
    failClosed();
    return false;
  }

  const liveEntries = existing.entries.filter((entry) => entry.expiresAtMs > now);
  if (liveEntries.length === 0) {
    clearDedicatedStore();
    return true;
  }
  if (liveEntries.length !== existing.entries.length) {
    if (!writeStore({ version: 1, entries: liveEntries })) {
      failClosed();
      return false;
    }
  }
  return true;
}

function decodeEntry(value: unknown, now: number): StoredAdminCustomerViewRoute | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const keys = Object.keys(value);
  if (keys.length !== STORE_FIELDS.size) return null;
  for (const key of keys) {
    if (!STORE_FIELDS.has(key)) return null;
  }
  const entry = value as Record<string, unknown>;
  if (!isValidAdminCustomerViewKey(entry.viewKey)) return null;
  if (!isExactActorUserId(entry.actorUserId)) return null;
  if (!isValidPlutusUserId(entry.targetUserId)) return null;
  if (!isSafeNonNegativeInteger(entry.lastAccessedAtMs)) return null;
  if (!isSafeNonNegativeInteger(entry.expiresAtMs)) return null;
  if (entry.lastAccessedAtMs > now) return null;
  const lifetime = entry.lastAccessedAtMs + ADMIN_CUSTOMER_VIEW_ROUTE_TTL_MS;
  if (!Number.isSafeInteger(lifetime)) return null;
  if (entry.expiresAtMs !== lifetime) return null;
  return {
    viewKey: entry.viewKey,
    actorUserId: entry.actorUserId,
    targetUserId: entry.targetUserId,
    expiresAtMs: entry.expiresAtMs,
    lastAccessedAtMs: entry.lastAccessedAtMs,
  };
}

function decodeStore(raw: unknown, now: number): StoredAdminCustomerViewRouteMap | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const keys = Object.keys(raw);
  if (keys.length !== 2 || !keys.includes("version") || !keys.includes("entries")) return null;
  const candidate = raw as { version?: unknown; entries?: unknown };
  if (candidate.version !== 1) return null;
  if (!Array.isArray(candidate.entries)) return null;
  if (candidate.entries.length > ADMIN_CUSTOMER_VIEW_ROUTE_CAP) return null;
  const entries: StoredAdminCustomerViewRoute[] = [];
  const seen = new Set<string>();
  for (const item of candidate.entries) {
    const decoded = decodeEntry(item, now);
    if (!decoded) return null;
    if (seen.has(decoded.viewKey)) return null;
    seen.add(decoded.viewKey);
    entries.push(decoded);
  }
  return { version: 1, entries };
}

function readStore(now: number): StoredAdminCustomerViewRouteMap | "missing" | "fail" {
  try {
    const raw = sessionStorage.getItem(ADMIN_CUSTOMER_VIEW_ROUTE_STORE_KEY);
    if (raw === null) return "missing";
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return "fail";
    }
    const decoded = decodeStore(parsed, now);
    return decoded ?? "fail";
  } catch {
    return "fail";
  }
}

function writeStore(map: StoredAdminCustomerViewRouteMap): boolean {
  try {
    sessionStorage.setItem(ADMIN_CUSTOMER_VIEW_ROUTE_STORE_KEY, JSON.stringify(map));
    return true;
  } catch {
    return false;
  }
}

function failClosed(): null {
  clearDedicatedStore();
  return null;
}

export function createAdminCustomerViewRouteMapping(
  actorUserId: string,
  targetUserId: string
): FrozenAdminCustomerViewRouteSubject | null {
  if (!isExactActorUserId(actorUserId) || !isValidPlutusUserId(targetUserId)) {
    return failClosed();
  }
  const now = nowMs();
  const existing = readStore(now);
  if (existing === "fail") return failClosed();
  if (
    existing !== "missing"
    && existing.entries.some((entry) => entry.actorUserId !== actorUserId)
  ) {
    return failClosed();
  }
  const liveEntries = existing === "missing"
    ? []
    : existing.entries.filter((entry) => entry.expiresAtMs > now);

  let viewKey: string | null = null;
  const used = new Set(liveEntries.map((entry) => entry.viewKey));
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const candidate = generateViewKey();
    if (!candidate) return failClosed();
    if (!used.has(candidate)) {
      viewKey = candidate;
      break;
    }
  }
  if (!viewKey) return failClosed();

  const sorted = [...liveEntries].sort((a, b) => a.lastAccessedAtMs - b.lastAccessedAtMs);
  while (sorted.length >= ADMIN_CUSTOMER_VIEW_ROUTE_CAP) {
    sorted.shift();
  }
  const created: StoredAdminCustomerViewRoute = {
    viewKey,
    actorUserId,
    targetUserId,
    lastAccessedAtMs: now,
    expiresAtMs: now + ADMIN_CUSTOMER_VIEW_ROUTE_TTL_MS,
  };
  if (!Number.isSafeInteger(created.expiresAtMs)) return failClosed();
  const next: StoredAdminCustomerViewRouteMap = {
    version: 1,
    entries: [...sorted, created],
  };
  if (!writeStore(next)) return failClosed();
  return { routeSubjectKey: viewKey, targetUserId };
}

export function resolveAdminCustomerViewRouteSubject(
  viewKey: string,
  actorUserId: string
): FrozenAdminCustomerViewRouteSubject | null {
  if (!isValidAdminCustomerViewKey(viewKey) || !isExactActorUserId(actorUserId)) {
    return failClosed();
  }
  const now = nowMs();
  const existing = readStore(now);
  if (existing === "missing" || existing === "fail") return failClosed();
  if (existing.entries.some((entry) => entry.actorUserId !== actorUserId)) {
    return failClosed();
  }

  const match = existing.entries.find((entry) => entry.viewKey === viewKey);
  if (!match) return failClosed();
  if (match.actorUserId !== actorUserId) return failClosed();
  if (match.expiresAtMs <= now) return failClosed();

  const renewed: StoredAdminCustomerViewRoute = {
    ...match,
    lastAccessedAtMs: now,
    expiresAtMs: now + ADMIN_CUSTOMER_VIEW_ROUTE_TTL_MS,
  };
  if (!Number.isSafeInteger(renewed.expiresAtMs)) return failClosed();
  const next: StoredAdminCustomerViewRouteMap = {
    version: 1,
    entries: existing.entries.map((entry) => (
      entry.viewKey === viewKey ? renewed : entry
    )),
  };
  if (!writeStore(next)) return failClosed();
  return {
    routeSubjectKey: match.viewKey,
    targetUserId: match.targetUserId,
  };
}
